/* @flow */

import VanillaToasts from 'vanillatoasts';
import { createSelector } from 'reselect';
import { type State, type GetState } from '../preact-app/types';
import { type Entregadores } from '../entregadores/types';
import { type EtapasId, type Pedido } from '../pedidos/types';
import { type AtendimentoState, type ViewListaPedido } from './atendimento-types';
import api from '../utils/api';
import * as notificacoes from './notificacoes';
import removerFormatacaoDinheiro from '../utils/remover-formatacao-dinheiro';
import { ETAPA_EXECUCAO, ETAPAS_EM_ANDAMENTO, ETAPA_ENTREGA } from '../pedidos/constants';
import criarEtapa from '../pedidos/criar-etapa';
import swalAlterarEtapa from '../pedidos/alterar-etapa/swalAlterarEtapa';
import swalAlterarEtapaEntrega from '../pedidos/alterar-etapa/swalAlterarEtapaEntrega';
import applyLoading from '../utils/apply-loading';
import imprimirPorPedidosId from '../atendimento/imprimir-por-pedidos-id';
import fecharSidebarParaCaber from '../atendimento/fechar-sidebar-para-caber';
import sleep from '../utils/sleep';
import alertas from '../alertas';
import filtrarPedidosPorEtapasId from '../pedidos/filtrar-pedidos-por-etapas-id';
import filtrarPedidosPorTermoPesquisa from '../pedidos/filterar-pedidos-por-termo-pesquisa';
import criarViewListaPedido from '../atendimento/criar-view-list-pedido';
import * as empresa from './empresa';
import * as loggi from './loggi';
import { type EstimarPedidoVariables } from '../loggi/types';
import getPackagesDoPedido from '../loggi/getPackagesDoPedido';
import converterEnderecoEmAddress from '../loggi/converterEnderecoEmAddress';
import estimateCreateOrder from '../loggi/estimateCreateOrder';
import createOrder from '../loggi/createOrder';
import { toastError } from '../toasts';

type SetIdPedidoDetalheAction = {
    type: 'atendimento/SET_ID_PEDIDO_DETALHE',
    id: ?number,
};

type SetFiltroEtapaAction = {
    type: 'atendimento/SET_FILTRO_ETAPA',
    filtroEtapa: ?EtapasId,
}

type SetTermoPesquisaAction = {
    type: 'atendimento/SET_TERMO_PESQUISA',
    termoPesquisa: ?string,
}

type Action =
    | SetIdPedidoDetalheAction
    | SetFiltroEtapaAction
    | SetTermoPesquisaAction;


// Action Types

const SET_ID_PEDIDO_DETALHE = 'atendimento/SET_ID_PEDIDO_DETALHE';
const SET_FILTRO_ETAPA = 'atendimento/SET_FILTRO_ETAPA';
const SET_TERMO_PESQUISA = 'atendimento/SET_TERMO_PESQUISA';

// Action Creator

export const setIdPedidoDetalhe = (id: ?number): SetIdPedidoDetalheAction => ({
    type: SET_ID_PEDIDO_DETALHE,
    id,
});

export const setFiltroEtapa = (filtroEtapa: ?EtapasId): SetFiltroEtapaAction => ({
    type: SET_FILTRO_ETAPA,
    filtroEtapa,
});

export const setTermoPesquisa = (termoPesquisa: ?string): SetTermoPesquisaAction => ({
    type: SET_TERMO_PESQUISA,
    termoPesquisa,
});

const editarPedido = applyLoading(api.editarPedido);
const createOrderLoading = applyLoading(createOrder);

// Selectors

const getAtendimento = (state: State): AtendimentoState => state.atendimento;
export const getTermoPesquisa = (state: State) => getAtendimento(state).termoPesquisa;
export const getFiltroEtapa = (state: State) => getAtendimento(state).filtroEtapa;
export const getIdPedidosDetalhe = (state: State) => getAtendimento(state).idPedidoDetalhe;

const getEntregadorPorId = (state: State, entregadoresId: ?number): ?Entregadores => (
    empresa.getEntregadores(state).find(({ id }) => id === entregadoresId)
);

export const getPedidosFiltrados: State => Pedido[] = createSelector(
    notificacoes.getPedidos,
    getTermoPesquisa,
    getFiltroEtapa,
    (pedidos, termoPesquisa, filtroEtapas) => pedidos
        .filter(filtrarPedidosPorEtapasId(filtroEtapas ? [filtroEtapas] : ETAPAS_EM_ANDAMENTO))
        .filter(filtrarPedidosPorTermoPesquisa(termoPesquisa)),
);

export const getListaViewPedido: State => ViewListaPedido[] = createSelector(
    getPedidosFiltrados,
    pedidos => pedidos.map(criarViewListaPedido),
);

export const getPedidosDetalhe: State => ?Pedido = createSelector(
    notificacoes.getPedidos,
    getIdPedidosDetalhe,
    (pedidos, idPedidoDetalhe) => (
        idPedidoDetalhe
            ? pedidos.find(({ id }) => id === idPedidoDetalhe)
            : null
    ),
);

const isPedidoEmExecucao: State => boolean = createSelector(
    getPedidosDetalhe,
    pedido => !!pedido && pedido.etapasId === ETAPA_EXECUCAO,
);

export const getIdPedidosDetalhesInicial: State => ?number = createSelector(
    notificacoes.getPedidosEmAndamento,
    (pedidos: Pedido[]) => (pedidos[0] ? pedidos[0].id : null),
);

export const getVariablesEstimarPedido: State => ?EstimarPedidoVariables = createSelector(
    loggi.getPk,
    getPedidosDetalhe,
    empresa.getEndereco,
    (shopId, pedido, endereco) => {
        if (!pedido) {
            return null;
        }

        const packages = getPackagesDoPedido(pedido);
        if (!packages.length || !endereco || !shopId) {
            return null;
        }

        return {
            shopId,
            packages,
            pickups: [
                {
                    address: converterEnderecoEmAddress(endereco),
                },
            ],
        };
    },
);

// Thunks

export const setIdPedidoDetalheThunk = (pedidosId: ?number) => (
    dispatch: Function,
    getState: GetState,
) => {
    if (pedidosId === getIdPedidosDetalhe(getState())) {
        return;
    }

    dispatch(setIdPedidoDetalhe(null));
    window.requestAnimationFrame(() => {
        sleep(500).then(() => {
            dispatch(setIdPedidoDetalhe(pedidosId));
        });
    });
};

export const carregamentoInicialThunk = () => (dispatch: Function, getState: GetState) => {
    fecharSidebarParaCaber();

    dispatch(empresa.buscarImpressao());
    dispatch(empresa.buscarFormasPagamento());
    dispatch(empresa.buscarEntregadores());
    dispatch(empresa.buscarEndereco());

    const urlParams = new URLSearchParams(window.location.search);
    const idPedido = urlParams.get('idPedido');

    if (idPedido) {
        dispatch(setIdPedidoDetalheThunk(Number(idPedido)));
    } else {
        dispatch(setIdPedidoDetalheThunk(getIdPedidosDetalhesInicial(getState())));
    }
};

export const alterarFormaPagamento = (formasPagamentoId: string) => (
    dispatch: Function,
    getState: GetState,
) => {
    const formaPagamento = empresa.getFormasPagamento(getState())
        .find(({ id }) => id === Number(formasPagamentoId));
    const pedidosId = getIdPedidosDetalhe(getState());

    if (!formaPagamento || !pedidosId) {
        return Promise.resolve();
    }

    return editarPedido(pedidosId, {
        formasPagamentoId: formaPagamento.id,
    }).then(() => {
        dispatch(notificacoes.alterarFormaPagamento(pedidosId, formaPagamento));
    });
};

const impressaoAutomatica = () => (dispatch: Function, getState: GetState) => {
    const pedidosId = getIdPedidosDetalhe(getState());
    if (empresa.isImpressaoAutomatica(getState()) && isPedidoEmExecucao(getState()) && pedidosId) {
        imprimirPorPedidosId(pedidosId);
    }
};

const getTotalEstimate = async (variables: ?EstimarPedidoVariables) => {
    if (!variables) {
        return Promise.resolve({
            totalCost: null,
            totalEta: null,
        });
    }
    return estimateCreateOrder(variables)
        .then(({ data }) => data.estimateCreateOrder.totalEstimate);
};

const criarPedidoNaLoggi = async (variables: ?EstimarPedidoVariables): Promise<number> => {
    if (!variables) {
        return Promise.reject(new Error('Não foi possível alterar a etapa, faltam dados no pedido.'));
    }

    return createOrderLoading(variables).then(({ data }) => {
        if (data.createOrder.errors) {
            return Promise.reject(new Error(data.createOrder.errors[0].message));
        }

        if (!data.createOrder.success) {
            return Promise.reject(new Error('Não foi possível criar pedido na loggi.'));
        }

        return data.createOrder.orders.pk;
    });
};

const alterarEtapa = (
    pedidosId: number,
    etapasId: EtapasId,
) => (
    dispatch: Function,
) => editarPedido(pedidosId, { etapasId })
    .then(() => dispatch(notificacoes.alterarEtapa(pedidosId, criarEtapa(etapasId))))
    .catch((error) => {
        alertas.erroAlterarEtapa(error);
        return Promise.reject();
    });

const alterarEtapaEntregaEntregadoresId = (
    pedido: Pedido,
    pedidosId: number,
    entregadoresId: number,
) => async (
    dispatch: Function,
    getState: GetState,
) => editarPedido(pedidosId, {
    pedido,
    etapasId: ETAPA_ENTREGA,
    entregadoresId,
})
    .then(() => {
        dispatch(notificacoes.alterarEtapa(pedidosId, criarEtapa(ETAPA_ENTREGA)));
        const entregador = getEntregadorPorId(getState(), entregadoresId);
        if (entregador) {
            dispatch(notificacoes.alterarEntregador(pedidosId, entregador));
        }
    })
    .catch((error) => {
        alertas.erroAlterarEtapa(error);
        return Promise.reject();
    });

const alterarEtapaEntregaLoggi = (
    pedidosId: number,
) => (dispatch: Function, getState: GetState) => {
    criarPedidoNaLoggi(getVariablesEstimarPedido(getState()))
        .then(loggiOrderId => editarPedido(pedidosId, {
            etapasId: ETAPA_ENTREGA,
            loggiOrderId,
        }))
        .then(() => {
            dispatch(notificacoes.alterarEtapa(pedidosId, criarEtapa(ETAPA_ENTREGA)));
        })
        .catch((error) => {
            if (error.response && error.response.data) {
                toastError.fire(error.response.data.message);
            } else {
                toastError.fire(error.message);
            }
            return Promise.reject();
        });
};

export const alterarTroco = (value: string) => (
    dispatch: Function,
    getState: GetState,
) => {
    const troco = removerFormatacaoDinheiro(value);
    const pedido = getPedidosDetalhe(getState());
    if (!pedido) {
        return Promise.resolve();
    }

    if (troco < pedido.valor) {
        VanillaToasts.create({
            type: 'danger',
            title: 'O valor do troco deve ser maior que o valor do pedido.',
            timeout: 5000,
        });
        return Promise.resolve();
    }

    return editarPedido(pedido.id, {
        troco,
    }).then(() => {
        dispatch(notificacoes.alterarTroco(pedido.id, troco));
    });
};

export const onAlterarEtapaEntrega = () => async (
    dispatch: Function,
    getState: GetState,
) => {
    const pedido = getPedidosDetalhe(getState());
    if (!pedido) {
        return;
    }

    const totalEstimate = await getTotalEstimate(getVariablesEstimarPedido(getState()));
    const { isLoggi, entregadoresId } = await swalAlterarEtapaEntrega({
        entregadores: empresa.getEntregadoresAtivos(getState()),
        pedido,
        ...totalEstimate,
    });

    if (isLoggi) {
        dispatch(alterarEtapaEntregaLoggi(pedido.id));
        return;
    }

    if (entregadoresId) {
        dispatch(alterarEtapaEntregaEntregadoresId(pedido, pedido.id, entregadoresId));
        return;
    }

    dispatch(alterarEtapa(pedido.id, ETAPA_ENTREGA));
};

export const onAlterarEtapa = (e: Event, etapasId: EtapasId) => async (
    dispatch: Function,
    getState: GetState,
) => {
    const pedido = getPedidosDetalhe(getState());
    if (!pedido || pedido.etapasId >= etapasId) {
        return;
    }

    if (etapasId === ETAPA_ENTREGA) {
        dispatch(onAlterarEtapaEntrega());
        return;
    }

    await swalAlterarEtapa(etapasId);
    await dispatch(alterarEtapa(pedido.id, etapasId));
    dispatch(impressaoAutomatica());
};

const initialState = {
    idPedidoDetalhe: null,
    filtroEtapa: null,
    termoPesquisa: null,
};

const atendimentoReducer = (
    state: AtendimentoState = initialState,
    action: Action,
): AtendimentoState => {
    switch (action.type) {
    case SET_ID_PEDIDO_DETALHE:
        return {
            ...state,
            idPedidoDetalhe: action.id,
        };
    case SET_TERMO_PESQUISA:
        return {
            ...state,
            filtroEtapa: null,
            termoPesquisa: action.termoPesquisa,
        };
    case SET_FILTRO_ETAPA:
        return {
            ...state,
            termoPesquisa: null,
            filtroEtapa: action.filtroEtapa,
        };
    default:
        return state;
    }
};

export default atendimentoReducer;
